import SEO from '@/components/SEO';
import { CheckCircleIcon, UserGroupIcon, BriefcaseIcon, HeartIcon, SparklesIcon, ArrowRightIcon, UsersIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Scale, Stethoscope, Home, Briefcase, Heart, Users, Building2, Landmark, Dumbbell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SimpleProfessionalForm from '@/components/forms/SimpleProfessionalForm';
import { ImageOptimizer } from '@/components/seo/ImageOptimizer';
import BreadcrumbNavigation from '@/components/seo/BreadcrumbNavigation';
import InternalLinkingOptimizer from '@/components/seo/InternalLinkingOptimizer';

const ProfessionalNetwork = () => {
  const benefits = ["Reach more families actively looking for trusted professionals", "Build visibility in the SeniorThrive ecosystem", "Strengthen your credibility as part of a forward-thinking movement", "Access sponsorship and referral opportunities"];
  
  // Professional types with unique icons
  const professionalTypes = [
    { label: "Geriatric Care Managers", icon: Users },
    { label: "Professional Caregivers & Agencies", icon: Heart },
    { label: "Social Workers & Case Managers", icon: Briefcase },
    { label: "Elder Law Attorneys & Estate Planners", icon: Scale },
    { label: "Financial Advisors & Reverse Mortgage Specialists", icon: Landmark },
    { label: "Physical Therapists, Nurses, & Clinicians", icon: Stethoscope },
    { label: "Home Modification Contractors & Age-in-Place Consultants", icon: Home },
    { label: "Senior Center & Community Directors", icon: Building2 },
    { label: "Wellness & Lifestyle Professionals", icon: Dumbbell }
  ];

  const steps = [{
    title: "Tell us about yourself",
    description: "Fill out the form below.",
    icon: UserGroupIcon
  }, {
    title: "Get connected",
    description: "We'll match you with opportunities to collaborate, sponsor, or support families in need.",
    icon: BriefcaseIcon
  }, {
    title: "Grow your reach",
    description: "Be featured in our network, gain referrals, and join a movement that redefines aging.",
    icon: SparklesIcon
  }];
  return <>
      <SEO title="Join the SeniorThrive Professional Network - Connect with Families & Older Adults" description="Join the SeniorThrive Professional Network. Connect with aging-in-place specialists, home safety pros, and senior care providers." keywords="professional network, aging professionals, senior care network, geriatric care managers, elder law attorneys, home care professionals, financial advisors seniors" canonical="https://seniorthrive.com/professional-network" />
      
      <BreadcrumbNavigation />

      {/* Hero Section */}
      <section className="relative text-white py-32 overflow-hidden">
        <div className="absolute inset-0">
          <ImageOptimizer 
            src="/lovable-uploads/dcb2c6cd-1245-4f41-8b12-008c364c9337.png"
            alt="Professional consultation meeting between advisor and senior client discussing aging-in-place solutions"
            className="w-full h-full object-cover"
            imgClassName="w-full h-full object-cover"
            priority={true}
            sizes="100vw"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/70 via-primary/60 to-primary-hover/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/40 via-transparent to-primary/40" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="mb-6 inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <HeartIcon className="h-6 w-6 text-white" />
              <span className="text-white font-medium">Care, Aging and Longevity Professionals</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-bold mb-8 leading-tight tracking-tight">
              You Are the Human Element of the
              <br />
              <span className="text-gradient bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                Home Operating System.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-6 leading-relaxed max-w-4xl mx-auto">
              Your expertise turns home and wellness data into real-world action families can trust.
            </p>
            <p className="text-base md:text-lg text-white/70 mb-12 leading-relaxed max-w-3xl mx-auto">
              SeniorThrive connects professionals like you with families who need clarity, confidence, and follow-through at home.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <SparklesIcon className="h-6 w-6 text-white" />
                <span className="text-lg font-medium text-white">Join the movement redefining aging</span>
              </div>
              <ArrowRightIcon className="h-6 w-6 text-white/60 rotate-90 sm:rotate-0" />
              <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <UserGroupIcon className="h-6 w-6 text-white" />
                <span className="text-lg font-medium text-white">Build meaningful connections</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-medium mb-6">
                <SparklesIcon className="h-4 w-4" />
                <span>Why Join Our Network</span>
              </div>
              <h2 className="text-5xl font-bold text-foreground mb-8 leading-tight">
                Amplify Your <span className="text-gradient">Impact</span>
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-4xl mx-auto">
                Plug your practice into our safety loop. Get data-driven referrals and provide the "All Clear" families need.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => <Card key={`benefit-${benefit.slice(0, 20)}-${index}`} className="modern-card group hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <CheckCircleIcon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <span className="text-lg font-semibold text-foreground leading-relaxed">{benefit}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </section>

      {/* Who We Welcome Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-medium mb-6">
                <UserGroupIcon className="h-4 w-4" />
                <span>Professional Community</span>
              </div>
              <h2 className="text-5xl font-bold text-foreground mb-8 leading-tight">
                Who We <span className="text-gradient">Welcome</span>
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">Our network is designed for professionals dedicated to caring for and empowering  older adults at home.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {professionalTypes.map((type, index) => {
                const IconComponent = type.icon;
                return (
                  <Card key={`type-${type.label.slice(0, 15)}-${index}`} className="feature-card group hover:shadow-md hover:border-primary/30">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="font-semibold text-foreground text-lg leading-tight">{type.label}</h3>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="modern-card bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-10 text-center">
                
                <p className="text-xl font-semibold text-foreground leading-relaxed">
                  If your work helps older adults live stronger, safer, and more connected at home—you have a place here.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-b from-muted/20 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-medium mb-6">
                <BriefcaseIcon className="h-4 w-4" />
                <span>Getting Started Is Fast & Free</span>
              </div>
              <h2 className="text-5xl font-bold text-foreground mb-8 leading-tight">
                How It <span className="text-gradient">Works</span>
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                Getting started is simple. Join our network in three easy steps.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Connection lines for desktop */}
              <div className="hidden md:block absolute top-24 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 transform -translate-x-1/2 -translate-y-1/2" />
              
              {steps.map((step, index) => {
              const IconComponent = step.icon;
              return <Card key={index} className="modern-card text-center relative group hover:shadow-xl">
                    <CardContent className="p-8">
                      <div className="relative mb-8">
                        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-primary to-primary-hover rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <IconComponent className="h-12 w-12 text-white" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-primary">{index + 1}</span>
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">{step.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Band */}
      <section className="py-12 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-y border-primary/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="h-6 w-6 text-primary" />
              <span className="text-lg font-medium text-foreground">Trusted Network</span>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">500+</p>
              <p className="text-sm text-muted-foreground">Local OTs, Lawyers, and Case Managers</p>
            </div>
            <div className="flex items-center gap-3">
              <UsersIcon className="h-6 w-6 text-primary" />
              <span className="text-lg font-medium text-foreground">Growing Daily</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="modern-card bg-gradient-to-r from-card to-card/80 border-primary/20 shadow-xl">
              <CardContent className="p-12">
                <div className="text-center mb-12">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-medium mb-8">
                    <SparklesIcon className="h-4 w-4" />
                    <span>Ready to Join?</span>
                  </div>
                  <h2 className="text-5xl font-bold text-foreground mb-8 leading-tight">
                    Plug Into the <span className="text-gradient">Home OS</span>
                  </h2>
                  <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                    Thriving at home isn't just for families. It's for the professionals who support them, too. 
                    Join the SeniorThrive Professional Network and make your expertise part of the future of aging.
                  </p>
                </div>

                <SimpleProfessionalForm />
              </CardContent>
            </Card>
            
            {/* Internal Linking Section */}
            <div className="mt-16">
              <InternalLinkingOptimizer currentPage="professional-network" />
            </div>
          </div>
        </div>
      </section>
    </>;
};
export default ProfessionalNetwork;